import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  styled,
  Button,
  TextField,
  Tooltip,
  MenuItem,
  Pagination,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import SchemaIcon from "@mui/icons-material/Schema";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import CircularProgress from "@mui/material/CircularProgress";
import SyncDisabledIcon from "@mui/icons-material/SyncDisabled";
import BugReport from "@mui/icons-material/BugReport";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TimelineIcon from "@mui/icons-material/Timeline";
import HistoryIcon from "@mui/icons-material/History";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { EditHeader, Settings, StudioSelection, TSMenu } from "./Studio";
import {
  useTestCaseResultsQuery,
  useRecentTestCaseResultsQuery,
  useTSRQuery,
  useAddStepQuery,
  useExecuteTCQuery,
  useProductQuery,
  useModuleQuery,
  useTestCaseQuery,
  useGetEnvQuery,
  usedependenciesOverQuery,
  useInlineTestStepsMutation,
  useDeleteTestStepsMutation,
  useCreateTestCaseMutation,
  useMergeCopilotQuery,
  useExecuteMobileTCQuery,
  useChangeToAssertMutation,
  useBulkEditFlow,
  useBulkEditWaitTime,
  useBulkEditKeywordAction,
  useDebugExecuteTCQuery,
  useBulkEditCondLogic,
  useModuleAuthorizationQuery,
  useSwapTestStepMutation,
  useGetHiddenOrDeletedStepsMutation,
  useRemoveConditionalLogic,
  usedependenciesQuery,
} from "../useQuery";

import { ContextIcon, RefreshIcon, ExportIcon, VisiblityIcon } from "../components-new/icons";
import { CustomInput, IconContainer, PageTitle, TestItemCard } from "../components-new/common";
import EditTabs from "./Studio/EditTabs";
import {
  setEditItemIndex,
  setEditStep,
  setIsMobile,
  setSelectedComponent,
  setSelectedProduct,
  setSelectedTestCase,
  setSelectedTestCaseItem,
  setTestCases,
  setTsrData,
  setTabs,
} from "../Redux/TestAuthoring/TestAuthoring";
import useSaveStepQuery from "../useQuery/useSaveStepQuery";
import { useLocation, useNavigate } from "react-router-dom";
import { useItemWidth, useNotification, useUserPermissions } from "../hooks";
import CustomDialog from "../components-new/common/CustomDialog";
import { useQueryClient } from "@tanstack/react-query";
import { OpenInNewOutlined } from "@mui/icons-material";
import TestTableView from "../components-new/common/TestTableView";
import ViewToggle from "../components-new/common/ViewToggle";
import LoaderSpinner from "../components/common/LoaderSpinner";
import {
  AssertSubOptions,
  AssertTextOptions,
  CheckBoxOptions,
  DropDownOptions,
  KeywordData,
  StatusOptions,
  MOBILE_ONLY_KEYWORDS,
  COMMON_KEYWORDS,
} from "./Studio/constants";
import { getActionOptions } from "./Studio/Edit/helper";
import DebugConfigModal from "./Studio/DebugConfigModal";
import { setAllTestSteps, clearExecutedSteps } from "../Redux/ExecutionReport/ExecutionReportSlice";
import ConditionalLogicModal from "../components-new/common/ConditionalLogicModal";
import ReviewFlowModal from "../components-new/common/ReviewFlowModal";
import TestCaseSelector from "../components-new/common/TestCaseSelector";
import HiddenDeletedStepsModal from "./Studio/Modals/HiddenDeletedStepsModal";
import nogruntLogo from "../Assets/nogruntblacklogo.svg";

const StyledButton = styled(Button)`
  padding: 10px 20px;
  border-radius: 8px;
  text-transform: capitalize;
`;

const useTestCaseStack = () => {
  const [testCaseStack, setTestCaseStack] = useState([]);

  const addToStack = useCallback((testCase) => {
    setTestCaseStack((prevStack) => {
      // Only add if the test case is not already in the stack
      if (!prevStack.includes(testCase)) {
        return [...prevStack, testCase];
      }
      return prevStack;
    });
  }, []);

  const isInStack = useCallback(
    (testCase) => {
      return testCaseStack.includes(testCase);
    },
    [testCaseStack],
  );

  return { testCaseStack, addToStack, isInStack };
};

const InlineDependencyAction = ({
  selectedTestCase,
  selectedProduct,
  selectedModule,
  checkedItems,
  inlineTestSteps,
  notify,
  setCheckedItems,
  setShowInlineModal,
}) => {
  const [targetTestCase, setTargetTestCase] = useState(null);
  const [runInNewTab, setRunInNewTab] = useState(false);

  const handleInlineClick = () => {
    if (!selectedTestCase) {
      notify("Please select a test case", "warning");
      return;
    }

    inlineTestSteps({
      data: checkedItems.map((item) => item.idtest_step),
      params: {
        testCaseId: selectedTestCase,
        inlineTcId: targetTestCase.idtest_case,
        newTab: runInNewTab,
      },
    });
    setCheckedItems([]);
    setShowInlineModal(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2 }}>
      <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden", px: 2.5, py: 2 }}>
        <TestCaseSelector
          onTestCaseSelect={setTargetTestCase}
          initialValues={{
            productId: selectedProduct,
            moduleId: selectedModule,
          }}
        />
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={runInNewTab}
                onChange={(e) => setRunInNewTab(e.target.checked)}
                color="primary"
              />
            }
            label="Run in new tab"
          />
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setCheckedItems([]);
            setShowInlineModal(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ background: "#ff5336" }}
          onClick={handleInlineClick}
          disabled={!targetTestCase}
        >
          Replace Inline Test Case
        </Button>
      </Box>
    </Box>
  );
};

const TestStudio = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notify = useNotification();
  const queryClient = useQueryClient();
  const RouteState = useLocation();
  const containerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const testStepsContainerRef = useRef(null);
  const itemsPerRow = 4;
  const gap = 32;
  const itemWidth = useItemWidth(containerRef, itemsPerRow, gap);

  const selectedTestCase = useSelector((state) => state.authoringSlice.selectedTestCase);
  const selectedProduct = useSelector((state) => state.authoringSlice.selectedProduct);
  const selectedModule = useSelector((state) => state.authoringSlice.selectedComponent);
  const currentEditItemIndex = useSelector((state) => state.authoringSlice.editItemIndex);
  const isMobile = useSelector((state) => state.authoringSlice.isMobile);
  const [showDependencyModal, setShowDependencyModal] = useState(false);
  const [cycles, setCycles] = useState([]); // <-- store cycles here

  const [showDependent, setshowDependent] = useState(false);
  const [showDependency, setshowDependency] = useState(false);
  // check module authorization for user type 2
  const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
  const userType = userDetails?.usertype;

  const { data: moduleAuthorization } = useModuleAuthorizationQuery(selectedModule, {
    enabled: userType === 2 && !!selectedModule,
  });

  // get user permissions based on module authorization
  const { canEdit, canDelete, canBulkEdit, isReadOnly } = useUserPermissions({
    moduleId: selectedModule,
    moduleAuthorization,
  });

  const [refreshFlag, setRefreshFlag] = useState(true);
  const [editData, setEditData] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [pageTitle, setPageTitle] = useState("");
  const [testCaseId, setTestCaseId] = useState("");
  const [testCaseSearchKeyword, setTestCaseSearchKeyword] = useState("");
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState(testCaseSearchKeyword);

  useEffect(() => {
    if (!selectedTestCase) {
      setPageTitle("");
      setTestCaseId("");
    }
  }, [selectedTestCase]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchKeyword(testCaseSearchKeyword);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [testCaseSearchKeyword]);

  const [checkedItems, setCheckedItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssertModal, setShowAssertModal] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [showInlineModal, setShowInlineModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [testCaseName, setTestCaseName] = useState("");
  const [lastKnownRecording, setLastKnownRecording] = useState(null);
  const [dataa, setDataa] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [hasUserClearedFilters, setHasUserClearedFilters] = useState(false);

  const [showFlowModal, setShowFlowModal] = useState(false);
  const [showWaitTimeModal, setShowWaitTimeModal] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState("Positive");
  const [waitTime, setWaitTime] = useState("");
  const [waitType, setWaitType] = useState("Before");
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditValues, setBulkEditValues] = useState({
    keyword: "",
    action: "",
    subAction: "",
  });
  const [isProductSetInitially, setIsProductSetInitially] = useState(false);
  const { testCaseStack, addToStack, isInStack } = useTestCaseStack();
  const { data: products, refetch: refetchProductQuery } = useProductQuery();
  const { data: modules, refetch: refetchModuleQuery } = useModuleQuery(selectedProduct, {
    enabled: selectedProduct !== "" && selectedProduct !== 0,
  });

  const selectedModuleData = modules?.find((m) => String(m.idmodules) === String(selectedModule));
  const isModuleReadOnly =
    userType === 1
      ? false
      : Boolean(
          selectedModuleData?.permissions?.some(
            (p) => String(p.roleid) === String(userType) && p.permission === "ReadOnly",
          ),
        );
  const effectiveReadOnly = isReadOnly || isModuleReadOnly;
  // eslint-disable-next-line no-unused-vars
  const { data: envDetails, refetch: refetchEnvQuery } = useGetEnvQuery(selectedProduct);

  const { data: testCases, isLoading: isLoadingTestcase } = useTestCaseQuery(
    selectedModule,
    debouncedSearchKeyword,
    {
      enabled: selectedModule !== "" && selectedModule !== 0,
    },
  );

  const { data: dependenciesOver = [] } = usedependenciesOverQuery(testCaseId);
  const { data: dependencies = [] } = usedependenciesQuery(testCaseId);

  const [anchorEl, setAnchorEl] = useState(null);
  const {
    data: testCaseResults,
    isLoading,
    isSuccess,
  } = useTestCaseResultsQuery(selectedTestCase, {
    enabled: !!selectedTestCase,
  });
  const { mutate: addStep, isSuccess: isAddSuccess } = useAddStepQuery({
    onSuccess: () => {
      notify("Step added successfully", "success");
    },
    onError: (error) => {
      notify(error?.response?.data?.error || "Failed to add step", "error");
    },
  });
  const { mutate: saveStep, isSuccess: isSaveSuccess } = useSaveStepQuery({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });

      notify("Step saved successfully", "success");
    },
    onError: (error) => {
      notify(error?.response?.data?.error || "Step Update Failed", "error");

      const backendCycles = error?.response?.data?.cycles;
      if (backendCycles && Array.isArray(backendCycles) && backendCycles.length > 0) {
        setCycles(backendCycles); // <-- store full enriched cycles
        setShowDependencyModal(true); // <-- open modal
      }
    },
  });
  const { mutate: executeTest, isSuccess: isExecuteSuccess } = useExecuteTCQuery();
  const { mutate: executeMobileTc, isSuccess: isExecuteMobileTcSuccess } =
    useExecuteMobileTCQuery();
  const { mutate: deleteTestSteps } = useDeleteTestStepsMutation();
  const { mutate: changeToAssert } = useChangeToAssertMutation();

  const { mutate: inlineTestSteps } = useInlineTestStepsMutation({
    onSuccess: () => {
      notify("Test Steps Inlined Successfully", "success");
      queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });
      queryClient.invalidateQueries({ queryKey: ["latestRecordings"] });
    },
    onError: () => {
      notify("Failed to Inline Test Steps", "error");
    },
  });
  const { mutate: createTestCase } = useCreateTestCaseMutation();

  const { mutate: bulkEditFlow } = useBulkEditFlow();
  const { mutate: bulkEditWaitTime } = useBulkEditWaitTime();
  const { mutate: bulkEditKeywordAction } = useBulkEditKeywordAction();
  const { mutate: swapSteps, isPending: isSwappingSteps } = useSwapTestStepMutation({
    onSuccess: () => {
      notify("Steps swapped successfully", "success");
      queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });
      queryClient.invalidateQueries({ queryKey: ["latestRecordings"] });
      setCheckedItems([]);
    },
    onError: (error) => {
      notify(`Error swapping steps: ${error.message}`, "error");
    },
  });
  const { mutate: removeConditionalLogic } = useRemoveConditionalLogic();

  const { data: latestRecordings } = useRecentTestCaseResultsQuery();

  const { data: tsrData } = useTSRQuery(selectedTestCase);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = testCaseResults?.data?.slice(indexOfFirstItem, indexOfLastItem) || [];
  const totalPages = Math.ceil((testCaseResults?.data?.length || 0) / itemsPerPage);

  const isDebugMode = useSelector((state) => state.executionSlice.isDebugMode);
  const debugStepNumber = useSelector((state) => state.executionSlice.debugStepNumber);
  const debugMode = useSelector((state) => state.executionSlice.debugMode);
  const [showDebugModal, setShowDebugModal] = useState(false);

  const { mutate: debugExecuteTest } = useDebugExecuteTCQuery();

  const [showHiddenDeletedModal, setShowHiddenDeletedModal] = useState(false);
  const [hiddenDeletedData, setHiddenDeletedData] = useState({ hiddenSteps: [], deletedSteps: [] });

  const { mutate: fetchHiddenDeletedSteps } = useGetHiddenOrDeletedStepsMutation();

  const [showCondLogicModal, setShowCondLogicModal] = useState(false);

  const { mutate: bulkEditCondLogic } = useBulkEditCondLogic();

  const [showReviewFlowModal, setShowReviewFlowModal] = useState(false);
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    if (testStepsContainerRef.current) {
      testStepsContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTestCase]);

  useEffect(() => {
    const testCaseFromResults = testCaseResults?.testcase?.Test_Case;
    const testCaseFromLatestRecordings = latestRecordings?.testcase?.Test_Case;

    if (testCaseFromResults) {
      addToStack(testCaseFromResults);
    }

    // only auto-switch to latest recordings if:
    // 1. User hasn't manually selected a test case, OR
    // 2. User has cleared filters (explicit reset)
    // This prevents overwriting user's manual selection when navigating between pages
    const shouldAutoSwitch = (!selectedTestCase || hasUserClearedFilters) && !isProductSetInitially;

    if (testCaseFromLatestRecordings && shouldAutoSwitch) {
      if (!isInStack(testCaseFromLatestRecordings)) {
        addToStack(testCaseFromLatestRecordings);
        setLastKnownRecording(latestRecordings);
        dispatch(setSelectedProduct(latestRecordings.Product));
        dispatch(setSelectedComponent(latestRecordings.Modules));
        dispatch(setSelectedTestCase(latestRecordings.Testcase));

        setPageTitle(latestRecordings.testcase?.Test_Case);
        setTestCaseId(latestRecordings.testcase?.idtest_case);
        setTestCaseSearchKeyword(latestRecordings.testcase?.Test_Case);

        setRefreshFlag(true);
      }
    }
  }, [
    testCaseResults,
    latestRecordings,
    addToStack,
    isInStack,
    dispatch,
    hasUserClearedFilters,
    selectedTestCase,
    isProductSetInitially,
  ]);

  useEffect(() => {
    if (!pageTitle) return;

    setTestCaseSearchKeyword(pageTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (testCaseId) {
      setTestCaseSearchKeyword(testCaseId);
    }
  }, []);

  useEffect(() => {
    refetchProductQuery();
    if (RouteState?.state?.fromExecutionReports || RouteState?.state?.fromTestCaseList) {
      setRefreshFlag(false);
      dispatch(setSelectedProduct(RouteState?.state?.idproducts));
      dispatch(setSelectedComponent(RouteState?.state?.idmodules));
      dispatch(setSelectedTestCase(RouteState?.state?.idtest_case));
      setTestCaseSearchKeyword(RouteState?.state?.Test_Case);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [RouteState?.state, RouteState?.key, dispatch]);

  useEffect(() => {
    setCheckedItems([]);
  }, [selectedTestCase]);

  useEffect(() => {
    dispatch(setEditStep(""));
    dispatch(setEditItemIndex(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (testCaseResults) {
      dispatch(setTestCases(testCaseResults.data ?? []));
      if (testCaseResults.tabs) {
        dispatch(setTabs(testCaseResults.tabs));
      }
    }
  }, [dispatch, testCaseResults]);

  useEffect(() => {
    dispatch(setTsrData(tsrData));
  }, [dispatch, tsrData]);

  useEffect(() => {
    if (currentEditItemIndex != null) {
      const currentEditItem = testCaseResults?.data[currentEditItemIndex];

      setEditData(currentEditItem);
      dispatch(setEditStep(currentEditItem));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditItemIndex]);

  useEffect(() => {
    // don't auto-switch to latest recordings if:
    // 1. We're navigating from Execution Reports (will be handled by fromExecutionReports effect)
    // 2. Product is already set
    // 3. User has cleared filters
    if (
      !selectedProduct &&
      latestRecordings &&
      !isProductSetInitially &&
      !hasUserClearedFilters &&
      !RouteState?.state?.fromExecutionReports
    ) {
      dispatch(setSelectedProduct(latestRecordings.Product));
      dispatch(setSelectedComponent(latestRecordings.Modules));
      dispatch(setSelectedTestCase(latestRecordings.Testcase));
      setPageTitle(latestRecordings?.testcase?.Test_Case);
      setTestCaseId(latestRecordings.testcase?.idtest_case);
      setIsProductSetInitially(true);
      setTestCaseSearchKeyword(latestRecordings.testcase.Test_Case);
    }
  }, [
    latestRecordings,
    dispatch,
    selectedProduct,
    isProductSetInitially,
    hasUserClearedFilters,
    RouteState?.state?.fromExecutionReports,
  ]);

  useEffect(() => {
    if (isSuccess) {
      setPageTitle(testCaseResults.testcase.Test_Case);
      setTestCaseId(testCaseResults.testcase.idtest_case);
      setTestCaseSearchKeyword(testCaseResults.testcase.Test_Case);
      dispatch(setSelectedTestCaseItem(testCaseResults.testcase));
      dispatch(setIsMobile(testCaseResults?.testcase?.case_Type === "MOBILE"));
    }
  }, [dispatch, isSuccess, selectedProduct, testCaseResults]);

  useEffect(() => {
    if (isExecuteSuccess || isExecuteMobileTcSuccess)
      navigate("/new/reports", {
        state: {
          isExecuteClick: true,
          fromTestStudio: true,
          idproducts: selectedProduct,
          idmodules: selectedModule,
          idtest_case: selectedTestCase,
          Test_Case: decodeURI(testCaseResults.testcase.Test_Case),
        },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExecuteSuccess, isExecuteMobileTcSuccess]);

  useEffect(() => {
    if (currentEditItemIndex === null && scrollPositionRef.current > 0) {
      if (testStepsContainerRef.current) {
        testStepsContainerRef.current.scrollTo({
          top: scrollPositionRef.current,
          behavior: "instant",
        });
      }
    }
  }, [currentEditItemIndex]);

  useEffect(() => {
    if (testCaseResults?.data) {
      dispatch(setAllTestSteps(testCaseResults.data));
      dispatch(clearExecutedSteps());
    }
  }, [dispatch, testCaseResults?.data]);

  const getNextNum = (testCaseResults, currentStepNum) => {
    if (!testCaseResults || !testCaseResults?.stepnums) return null;
    const { stepnums } = testCaseResults;
    const currentSeq = Object.entries(stepnums).find(
      ([seq, stepNum]) => stepNum === currentStepNum,
    )?.[0];
    const nextSeq = currentSeq ? String(Number(currentSeq) + 1) : null;
    return stepnums[nextSeq] ?? currentStepNum;
  };

  const handleEdit = (item) => {
    setRefreshFlag(false);
    const currenItemIndex = testCaseResults.data.findIndex(
      (testCaseItem) => testCaseItem.Step_Number === item.Step_Number,
    );
    if (testStepsContainerRef.current) {
      scrollPositionRef.current = testStepsContainerRef.current.scrollTop;
    }
    dispatch(setEditItemIndex(currenItemIndex));
  };

  const nextNum = testCaseResults ? getNextNum(testCaseResults, editData?.Step_Number) : null;
  const data = {
    tcid: editData?.Test_Case_Id,
    stepNumber: editData?.Step_Number,
    nextStepNumber: nextNum,
    tcname: testCaseResults?.testcase?.Test_Case,
    envurl: encodeURIComponent(testCaseResults?.data[0]?.TestData),
  };

  const { mutate: modalExecute } = useMergeCopilotQuery(data, {});

  const handleMerge = () => {
    modalExecute(data);
    //close the menu
  };

  const handleExecuteTest = (isBaseline, action) => {
    try {
      const selectedItem = testCaseResults.testcase;

      if (selectedItem.case_Type === "MOBILE") {
        executeMobileTc({
          Test_Case_Id: selectedItem.idtest_case,
          isDebug: isDebugMode,
          debugStepNumber: debugStepNumber,
          debugMode: debugMode,
        });
        //navigate user to reports page
        navigate("/new/reports", {
          state: {
            isExecuteClick: true,
            fromTestStudio: true,
            idproducts: selectedProduct,
            idmodules: selectedModule,
            idtest_case: selectedItem.idtest_case,
            Test_Case: decodeURI(selectedItem.Test_Case),
            isDebug: isDebugMode,
            debugStepNumber: debugStepNumber,
            debugMode: debugMode,
          },
        });
        return;
      }

      const obj = {
        id: selectedItem.idtest_case,
        SelectEnvironmentName: selectedItem.envname,
        SelectEnvironmentUrl: selectedItem.envurl,
        SelectBrowserName: selectedItem.browser,
        SelectResolution: selectedItem.SelectResolution,
        SelectRunonce: selectedItem.multirun,
        TcThreshold: selectedItem.testcasethreshold,
        testcase: selectedItem?.Test_Case,
        proxyurl: selectedItem.proxyurl,
        isDebug: isDebugMode,
        debugStepNumber: debugStepNumber,
        debugMode: debugMode,
      };

      if (isBaseline) {
        obj.baseline = isBaseline;
      }

      if (action) {
        obj.action = action;
      }

      executeTest(obj);
    } catch (error) {
      console.error("Error executing test:", error);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const addStepQuery = (payload) => {
    addStep(payload, {
      onSuccess: () => {
        notify("Step cloned successfully", "success");
      },
      onError: (error) => {
        notify(error?.response?.data?.error || "Failed to clone step", "error");
      },
    });
  };
  const saveStepQuery = (payload) => {
    saveStep(payload);
  };

  const handleCheckboxChange = (e, item) => {
    if (e.target.checked) {
      setCheckedItems((prev) => [...prev, item]);
    } else {
      setCheckedItems((prev) =>
        prev.filter((testStep) => testStep.idtest_step !== item.idtest_step),
      );
    }
  };

  const handleDeleteTestSteps = () => {
    if (!checkedItems.length) {
      notify("Please select steps to delete", "warning");
      return;
    } else {
      setShowDeleteModal(true);
    }
  };

  const handleFindSimilarSteps = (suiteId) => {
    if (checkedItems.length !== 1) {
      notify("Please select exactly one step to find similar steps", "warning");
      return;
    }
    const step = checkedItems[0];
    const searchParams = new URLSearchParams();
    searchParams.set("stepId", step.idtest_step);
    if (suiteId) searchParams.set("testSuiteId", suiteId);

    window.open(`/new/similar-test-steps?${searchParams.toString()}`, "_blank");
  };

  const handleSwapSteps = () => {
    if (checkedItems.length !== 2) {
      notify("Please select exactly 2 steps to swap", "warning");
      return;
    }
    const [step1, step2] = checkedItems;
    swapSteps({
      step1Id: step1.idtest_step,
      step2Id: step2.idtest_step,
      tcid: step1.Test_Case_Id,
    });
  };

  const handleChangeToAssert = () => {
    if (!checkedItems.length) {
      notify("Please select steps to Change to Assertion", "warning");
      return;
    } else {
      setShowAssertModal(true);
    }
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const handleInlineTestSteps = () => {
    if (!checkedItems.length) {
      notify("Please select steps to be used for creating inline dependence...", "warning");
      return;
    } else {
      setShowInlineModal(true);
    }
  };

  const handleCreateTestCase = () => {
    if (!checkedItems.length) {
      notify("Please select steps to create...", "warning");
      return;
    } else {
      setShowCreateModal(true);
    }
  };

  const handleBulkEditKeywordAction = () => {
    if (!checkedItems.length) {
      notify("Please select steps to edit", "warning");
      return;
    }
    setShowBulkEditModal(true);
  };

  const handleBulkEditSave = () => {
    const data = {
      testStepIds: checkedItems.map((item) => item.idtest_step),
    };

    bulkEditKeywordAction(
      {
        testCaseId: selectedTestCase,
        data,
        Keyword: bulkEditValues.keyword,
        Action: bulkEditValues.action,
        subAction: bulkEditValues.subAction,
      },
      {
        onSuccess: () => {
          notify("Keyword & Action updated successfully", "success");
          queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });
          queryClient.invalidateQueries({ queryKey: ["latestRecordings"] });
          setShowBulkEditModal(false);
          setCheckedItems([]);
        },
        onError: (error) => {
          notify(`Error updating Keyword & Action: ${error.message}`, "error");
        },
      },
    );
  };

  const handleFlowSave = () => {
    const testCaseId = selectedTestCase;
    const data = {
      testStepIds: checkedItems.map((item) => item.idtest_step),
    };

    bulkEditFlow(
      { testCaseId, data, flow: selectedFlow },
      {
        onSuccess: () => {
          notify(
            `Flow status updated to ${StatusOptions.find((option) => option.value === selectedFlow)?.label || ""} successfully`,
            "success",
          );
          queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });
          queryClient.invalidateQueries({ queryKey: ["latestRecordings"] });
          setShowFlowModal(false);
          setCheckedItems([]);
        },
        onError: (error) => {
          notify(`Error updating flow status: ${error.message}`, "error");
          setShowFlowModal(false);
        },
      },
    );
  };

  const handleWaitTimeSave = () => {
    const testCaseId = selectedTestCase;
    const data = {
      testStepIds: checkedItems.map((item) => item.idtest_step),
    };

    bulkEditWaitTime(
      { testCaseId, waittime: waitTime, wait: waitType, data },
      {
        onSuccess: () => {
          notify(`Wait time updated successfully`, "success");
          queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });
          queryClient.invalidateQueries({ queryKey: ["latestRecordings"] });
          setShowWaitTimeModal(false);
          setCheckedItems([]);
          setWaitTime("");
        },
        onError: (error) => {
          notify(`Error updating wait time: ${error.message}`, "error");
          setShowWaitTimeModal(false);
        },
      },
    );
  };

  const handleSetFlow = () => {
    if (!checkedItems.length) {
      notify("Please select steps to set flow status", "warning");
      return;
    }
    setShowFlowModal(true);
  };

  const handleSetWaitTime = () => {
    if (!checkedItems.length) {
      notify("Please select steps to set wait time", "warning");
      return;
    }
    setShowWaitTimeModal(true);
  };

  const handleSetCondLogic = () => {
    if (!checkedItems.length) {
      notify("Please select steps to set conditional logic", "warning");
      return;
    }
    setShowCondLogicModal(true);
  };

  const handleRemoveConditionalLogic = () => {
    if (!checkedItems.length) {
      notify("Please select steps to remove conditional logic", "warning");
      return;
    }

    removeConditionalLogic(
      {
        testCaseId: selectedTestCase,
        testStepIds: checkedItems.map((item) => item.idtest_step),
      },
      {
        onSuccess: () => {
          notify("Conditional logic removed successfully", "success");
          queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });
          queryClient.invalidateQueries({ queryKey: ["latestRecordings"] });
          setCheckedItems([]);
        },
        onError: (error) => {
          notify(error?.response?.data?.message || "Failed to remove conditional logic", "error");
        },
      },
    );
  };

  const handleCondLogicSave = (values) => {
    const testCaseId = selectedTestCase;
    const data = {
      testStepIds: checkedItems.map((item) => item.idtest_step),
    };

    bulkEditCondLogic(
      {
        testCaseId,
        data,
        condLogic: values.condLogic,
        condexp: values.condexp,
      },
      {
        onSuccess: () => {
          notify("Conditional logic updated successfully", "success");
          queryClient.invalidateQueries(["testCaseResults"]);
          queryClient.invalidateQueries(["latestRecordings"]);
          setCheckedItems([]);
        },
        onError: (error) => {
          notify(`Error updating conditional logic: ${error.message}`, "error");
        },
      },
    );
  };

  // for now we only allow to choose 2 step range (start and end step)
  const handleViewHiddenDeletedSteps = () => {
    if (checkedItems.length === 2) {
      fetchHiddenDeletedSteps(
        {
          testStepNumbers: checkedItems.map((item) => item.Step_Number),
          testCaseId: selectedTestCase,
        },
        {
          onSuccess: (response) => {
            setHiddenDeletedData(response);
            setShowHiddenDeletedModal(true);
          },
          onError: (error) => {
            console.error("Error fetching hidden/deleted steps:", error);
            notify("Failed to fetch hidden/deleted steps", "error");
          },
        },
      );
    }
  };

  const FlowAction = () => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            select
            label="Flow Status"
            value={selectedFlow}
            onChange={(e) => setSelectedFlow(e.target.value)}
            fullWidth
            variant="outlined"
          >
            {StatusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="text"
            sx={{ color: "#ff5336" }}
            onClick={() => {
              setCheckedItems([]);
              setShowFlowModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              background: "#ff5336",
              "&:hover": {
                background: "#e94a2f",
              },
            }}
            disabled={!selectedFlow}
            onClick={handleFlowSave}
          >
            Save
          </Button>
        </Box>
      </Box>
    );
  };

  const WaitTimeAction = () => {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <TextField
            select
            label="Wait Type"
            value={waitType}
            onChange={(e) => setWaitType(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
          >
            <MenuItem value="Before">Wait Before</MenuItem>
            <MenuItem value="After">Wait After</MenuItem>
            <MenuItem value="WaitTill">Wait Till Found</MenuItem>
            <MenuItem value="WaitForAllApi">Wait For All API</MenuItem>
            <MenuItem value="WaitForConfiguredApis">Wait For Configured APIs</MenuItem>
          </TextField>

          <TextField
            label="Enter Wait Time"
            variant="outlined"
            fullWidth
            type="number"
            value={waitTime}
            onChange={(e) => setWaitTime(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                e.preventDefault();
                const newValue =
                  e.key === "ArrowUp"
                    ? (parseInt(waitTime || "0") + 1).toString()
                    : (parseInt(waitTime || "0") - 1).toString();
                setWaitTime(newValue);
              }
            }}
          />
        </Box>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setCheckedItems([]);
            setShowWaitTimeModal(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ background: "#ff5336" }}
          disabled={!waitTime}
          onClick={handleWaitTimeSave}
        >
          Save
        </Button>
      </>
    );
  };

  const BulkEditAction = () => {
    const actionOptions = getActionOptions(bulkEditValues.keyword);

    const bulkKeywordData = KeywordData.filter((k) => {
      if (k.value === "Assertion") return false;
      if (COMMON_KEYWORDS.includes(k.value)) return true;
      if (isMobile) return MOBILE_ONLY_KEYWORDS.includes(k.value);
      return !MOBILE_ONLY_KEYWORDS.includes(k.value);
    });

    const showSubActionDropdown = () => {
      if (!bulkEditValues.keyword || !bulkEditValues.action) return false;

      const keyword = bulkEditValues.keyword;
      const action = bulkEditValues.action;

      return (
        (keyword === "Assertion" && action === "Properties") ||
        // (keyword === "Assertion" && action === "validatePartial") ||
        (keyword === "Validation" && action === "validatePartial") ||
        (keyword === "DropDown" && action === "Select") ||
        (keyword === "Check Box" && action === "Checkbox") ||
        (keyword === "Alert" && action === "Alert")
      );
    };

    const getSubActionOptions = () => {
      const keyword = bulkEditValues.keyword;
      const action = bulkEditValues.action;

      if (keyword === "Assertion" && action === "Properties") return AssertSubOptions;
      // if ((keyword === "Assertion" || keyword === "Validation") && action === "validatePartial")
      //   return AssertTextOptions;
      if (keyword === "DropDown" && action === "Select") return DropDownOptions;
      if (keyword === "Check Box" && action === "Checkbox") return CheckBoxOptions;
      if (keyword === "Alert" && action === "Alert") return AssertTextOptions;

      return [];
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2 }}>
        <TextField
          select
          fullWidth
          label="Element"
          value={bulkEditValues.keyword}
          onChange={(e) =>
            setBulkEditValues({
              ...bulkEditValues,
              keyword: e.target.value,
              action: "",
              subAction: "",
            })
          }
        >
          {bulkKeywordData.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          label="Action"
          value={bulkEditValues.action}
          onChange={(e) =>
            setBulkEditValues({
              ...bulkEditValues,
              action: e.target.value,
              subAction: "",
            })
          }
          disabled={!bulkEditValues.keyword}
        >
          {actionOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {showSubActionDropdown() && (
          <TextField
            select
            fullWidth
            label="Options"
            value={bulkEditValues.subAction}
            onChange={(e) =>
              setBulkEditValues({
                ...bulkEditValues,
                subAction: e.target.value,
              })
            }
          >
            {getSubActionOptions().map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
          <Button
            sx={{ color: "#ff5336" }}
            onClick={() => {
              setCheckedItems([]);
              setShowBulkEditModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ background: "#ff5336" }}
            onClick={handleBulkEditSave}
            disabled={!bulkEditValues.keyword || !bulkEditValues.action}
          >
            Save
          </Button>
        </Box>
      </Box>
    );
  };

  const DeleteAction = () => {
    return (
      <>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setCheckedItems([]);
            setShowDeleteModal(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ background: "#ff5336" }}
          onClick={() => {
            deleteTestSteps({
              data: checkedItems.map((item) => item.idtest_step),
              params: {
                testCaseId: selectedTestCase,
              },
            });
            setCheckedItems([]);
            setShowDeleteModal(false);
          }}
        >
          Delete
        </Button>
      </>
    );
  };
  const AssertAction = () => {
    return (
      <>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setCheckedItems([]);
            setShowAssertModal(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ background: "#ff5336" }}
          onClick={() => {
            changeToAssert({
              data: checkedItems.map((item) => item.idtest_step),
              params: {
                testCaseId: selectedTestCase,
              },
            });
            setCheckedItems([]);
            setShowAssertModal(false);
          }}
        >
          Change to Assert
        </Button>
      </>
    );
  };
  const DependencyContent = () => {
    const { inlineonTC = [], deponTC = [] } = dependencies || {};

    return (
      <>
        {/* Inline Dependency */}
        <Typography fontWeight="bold" mb={1}>
          Inline Dependency
        </Typography>

        {inlineonTC.length ? (
          inlineonTC.map((item, index) => {
            const displayText = `Step ${item.step_number} — ${item.product} — ${item.module} — ${item.test_case_name} (${item.idtest_case})`;

            return (
              <Typography
                key={`${item.idtest_case}-${index}`}
                title={displayText}
                sx={{
                  cursor: "pointer",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  background: "#f3f4f6",
                  fontSize: 13,
                  mb: 1,
                  wordBreak: "break-word",
                }}
              >
                {displayText}
              </Typography>
            );
          })
        ) : (
          <Typography color="text.secondary">No Inline dependency</Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Data Dependency */}
        <Typography fontWeight="bold" mb={1}>
          Data Dependency
        </Typography>

        {deponTC.length ? (
          deponTC.map((item, index) => {
            const displayText = `Step ${item.step_number} — ${item.product} — ${item.module} — ${item.test_case_name} (${item.idtest_case})`;

            return (
              <Typography
                key={`${item.idtest_case}-${index}`}
                title={displayText}
                sx={{
                  cursor: "pointer",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  background: "#f3f4f6",
                  fontSize: 13,
                  mb: 1,
                  wordBreak: "break-word",
                }}
              >
                {displayText}
              </Typography>
            );
          })
        ) : (
          <Typography color="text.secondary">No Data dependency</Typography>
        )}
      </>
    );
  };
  const DependencyContentover = () => {
    const renderRow = (item, index) => {
      const text = `${item.product} - ${item.module} - ${item.test_case_name} (${item.idtest_case})`;

      return (
        <Typography
          key={index}
          title={text}
          sx={{
            cursor: "pointer",
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            background: "#f3f4f6",
            fontSize: 13,
            whiteSpace: "nowrap",
            wordBreak: "break-word",
          }}
        >
          {text}
        </Typography>
      );
    };

    return (
      <>
        {/* Inline Dependency */}
        <Typography fontWeight="bold" mb={2}>
          Inline Dependencies
        </Typography>

        {dependenciesOver?.inlineonTC?.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {dependenciesOver.inlineonTC.map(renderRow)}
          </div>
        ) : (
          <Typography color="text.secondary" mb={3}>
            No Inline dependencies
          </Typography>
        )}

        <div style={{ marginTop: 20 }} />

        <Typography fontWeight="bold" mb={2}>
          Data Dependencies
        </Typography>

        {dependenciesOver?.deponTC?.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {dependenciesOver.deponTC.map(renderRow)}
          </div>
        ) : (
          <Typography color="text.secondary">No Data dependencies</Typography>
        )}
      </>
    );
  };
  const CreateAction = () => {
    return (
      <>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setCheckedItems([]);
            setShowCreateModal(false);
            setTestCaseName("");
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ background: "#ff5336" }}
          disabled={!testCaseName}
          onClick={() => {
            createTestCase(
              {
                data: {
                  testCaseName,
                  testSteps: checkedItems,
                  testCaseId: selectedTestCase,
                },
              },
              {
                onSuccess: (data) => {
                  notify("Test Case Created Successfully", "success");
                },
                onError: (error) => {
                  console.error("Mutation failed:", error);
                  notify(`Error:${error}`, "warning");
                },
              },
            );
            setCheckedItems([]);
            setShowCreateModal(false);
            setTestCaseName("");
          }}
        >
          Create
        </Button>
      </>
    );
  };

  const handleDebugExecute = (debugConfig) => {
    try {
      const selectedItem = testCaseResults.testcase;

      if (selectedItem.case_Type === "MOBILE") {
        executeMobileTc({
          Test_Case_Id: selectedItem.idtest_case,
          isDebug: debugConfig.isDebugMode,
          debugStepNumber: debugConfig.debugStepNumber,
          debugMode: debugConfig.debugMode,
          option: debugConfig.option,
          idtest_step: debugConfig.idtest_step,
        });
        navigate("/new/reports", {
          state: {
            isExecuteClick: true,
            fromTestStudio: true,
            idproducts: selectedProduct,
            idmodules: selectedModule,
            idtest_case: selectedItem.idtest_case,
            Test_Case: decodeURI(selectedItem.Test_Case),
            isDebug: debugConfig.isDebugMode,
            debugStepNumber: debugConfig.debugStepNumber,
            debugMode: debugConfig.debugMode,
            option: debugConfig.option,
            idtest_step: debugConfig.idtest_step,
          },
        });
        return;
      }

      const obj = {
        id: selectedItem.idtest_case,
        SelectEnvironmentName: selectedItem.envname,
        SelectEnvironmentUrl: selectedItem.envurl,
        SelectBrowserName: selectedItem.browser,
        SelectResolution: selectedItem.SelectResolution,
        SelectRunonce: selectedItem.multirun,
        TcThreshold: selectedItem.testcasethreshold,
        testcase: selectedItem?.Test_Case,
        proxyurl: selectedItem.proxyurl,
        baseline: false,
        isDebug: debugConfig.isDebugMode,
        debugStepNumber: debugConfig.debugStepNumber,
        debugMode: debugConfig.debugMode,
        option: debugConfig.option,
        idtest_step: debugConfig.idtest_step,
      };

      debugExecuteTest(obj, {
        onSuccess: () => {
          navigate("/new/reports", {
            state: {
              isExecuteClick: true,
              fromTestStudio: true,
              idproducts: selectedProduct,
              idmodules: selectedModule,
              idtest_case: selectedItem.idtest_case,
              Test_Case: decodeURI(selectedItem.Test_Case),
              isDebug: debugConfig.isDebugMode,
              debugStepNumber: debugConfig.debugStepNumber,
              debugMode: debugConfig.debugMode,
              option: debugConfig.option,
              idtest_step: debugConfig.idtest_step,
            },
          });
        },
      });
    } catch (error) {
      console.error("Error executing test in debug mode:", error);
    }
  };

  const handleInlineTestCaseView = (testCaseId) => {
    dispatch(setSelectedTestCase(testCaseId));
    setTestCaseSearchKeyword(testCaseId);
    setRefreshFlag(false);
  };

  const renderDependencyActions = () => (
    <>
      <Button
        variant="contained"
        color="warning"
        onClick={() => {
          setShowDependencyModal(false);
          setshowDependent(false);
          setshowDependency(false);
        }}
      >
        Close
      </Button>
    </>
  );

  const renderDependencyContent = () => {
    if (!cycles || cycles.length === 0) return null;

    return (
      <>
        <Typography sx={{ mb: 2 }}>
          This update creates one or more circular dependency loops. Please review the affected test
          cases below:
        </Typography>

        <List>
          {cycles.map((cycle, idx) => (
            <ListItem
              key={idx}
              sx={{
                mb: 1,
                border: "1px solid",
                borderColor: "warning.light",
                borderRadius: 1,
                backgroundColor: "#fff3f3",
              }}
            >
              <ListItemText
                primary={`Cycle ${idx + 1}`}
                secondary={
                  <>
                    {cycle.map((tc, i) => (
                      <div key={i}>
                        <strong>TC-{tc.testCaseId}</strong> —{" "}
                        {`${tc.product || "N/A"} | ${tc.module || "N/A"}`}
                        {i < cycle.length - 1 && " → "}
                      </div>
                    ))}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  return (
    <Box
      sx={{
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {isSwappingSteps && (
        <LoaderSpinner size="xl" logo={nogruntLogo} text="Swap in progress..." overlay />
      )}
      <CustomDialog
        title={"Are you sure you want to delete selected test steps?"}
        open={showDeleteModal}
        actions={<DeleteAction />}
      />
      <CustomDialog
        title={"Are you sure you want to Create Inline Dependence for selected test steps?"}
        open={showInlineModal}
        actions={
          <InlineDependencyAction
            selectedTestCase={selectedTestCase}
            selectedProduct={selectedProduct}
            selectedModule={selectedModule}
            checkedItems={checkedItems}
            inlineTestSteps={inlineTestSteps}
            notify={notify}
            setCheckedItems={setCheckedItems}
            setShowInlineModal={setShowInlineModal}
          />
        }
      />
      <CustomDialog
        open={showDependent}
        width={530}
        onClose={() => setshowDependent(false)}
        title="Dependency"
        content={DependencyContentover()}
        actions={renderDependencyActions()}
      />
      <CustomDialog
        open={showDependency}
        width={530}
        onClose={() => setshowDependency(false)}
        title="Dependency"
        content={DependencyContent()}
        actions={renderDependencyActions()}
      />
      <CustomDialog
        title={"Are you sure you want to Change selected test steps to Assert?"}
        open={showAssertModal}
        actions={<AssertAction />}
      />
      <CustomDialog
        open={showDependencyModal}
        width={530}
        onClose={() => setShowDependencyModal(false)}
        title="Circular Dependencies Detected"
        content={renderDependencyContent()}
        actions={renderDependencyActions()}
      />

      <CustomDialog
        title={"Create Test Case"}
        open={showCreateModal}
        actions={<CreateAction />}
        content={
          <Box>
            <CustomInput
              labelinput="Test Case Name"
              placeholder="Enter test case name"
              onChange={(e) => setTestCaseName(e.target.value)}
              value={testCaseName}
            />
          </Box>
        }
      />
      <CustomDialog
        title={"Set Flow Status for Selected Test Steps"}
        open={showFlowModal}
        actions={<FlowAction />}
      />

      <CustomDialog
        title={"Set Wait Time for Selected Test Steps"}
        open={showWaitTimeModal}
        actions={<WaitTimeAction />}
      />

      <CustomDialog
        title={"Bulk Edit Keyword & Action"}
        open={showBulkEditModal}
        actions={<BulkEditAction />}
      />
      <ConditionalLogicModal
        open={showCondLogicModal}
        onClose={() => {
          setShowCondLogicModal(false);
          setCheckedItems([]);
        }}
        onSave={handleCondLogicSave}
      />
      <DebugConfigModal
        open={showDebugModal}
        onClose={() => setShowDebugModal(false)}
        onExecute={handleDebugExecute}
        testSteps={testCaseResults?.data || []}
        initialConfig={{
          isDebugMode,
          debugStepNumber,
          debugMode,
        }}
      />
      <ReviewFlowModal
        open={showReviewFlowModal}
        onClose={() => setShowReviewFlowModal(false)}
        tcid={selectedTestCase}
        latestRecordings={latestRecordings}
      />

      {/* Hidden & Deleted Steps Modal */}
      {showHiddenDeletedModal && (
        <HiddenDeletedStepsModal
          open={showHiddenDeletedModal}
          onClose={() => setShowHiddenDeletedModal(false)}
          data={hiddenDeletedData}
          stepRange={
            checkedItems.length === 2
              ? checkedItems.map((item) => item.Step_Number).sort((a, b) => a - b)
              : null
          }
        />
      )}

      {currentEditItemIndex === null && (
        <>
          <Box
            sx={{
              position: "fixed",
              zIndex: "10",
              width: "85%",
              right: "0px",
              left: "260px",
              background: "#f5f7f9",
            }}
          >
            <Box
              mb={2}
              display="flex"
              alignItems="center"
              justifyContent={"space-between"}
              pr={"20px"}
              gap={2}
              sx={{
                minWidth: 0,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                columnGap={2}
                py={1}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Tooltip title="Back to Test Case List">
                  <IconButton
                    onClick={() => navigate("/new/test-case-list")}
                    aria-label="Back to Test Case List"
                    sx={{
                      backgroundColor: "#2550d1",
                      color: "#fff",
                      padding: "8px",
                      "&:hover": { backgroundColor: "#1e40a8" },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>
                <PageTitle title={pageTitle} variant="h5" truncate maxWidth="100%" />
                <Tooltip title={`Test Case Id: ${testCaseId}`}>
                  <Typography
                    variant="h6"
                    color="#8089A8"
                    fontSize="13px"
                    fontWeight={500}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {testCaseId}
                  </Typography>
                </Tooltip>
                <Tooltip title={`Total Test Steps: ${testCaseResults?.data?.length || 0}`}>
                  <Typography
                    variant="h6"
                    color="#8089A8"
                    fontSize="13px"
                    fontWeight={500}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    ({testCaseResults?.data?.length || 0} steps)
                  </Typography>
                </Tooltip>
              </Box>
              {checkedItems?.length > 0 && (
                <Tooltip
                  title={`${checkedItems.length} test step${checkedItems.length !== 1 ? "s" : ""} selected`}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "32px",
                      height: "32px",
                      px: 1,
                      backgroundColor: "#547de8",
                      borderRadius: "6px",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "12px",
                      }}
                    >
                      {checkedItems.length}
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              <Box
                display={"flex"}
                columnGap={"1rem"}
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  marginRight: "20px",
                }}
              >
                <Settings />

                {/* 
                <ViewToggle view={viewMode} onViewChange={handleViewChange} />
                */}

                <Box>
                  {refreshFlag ? (
                    <IconContainer
                      handleClick={() => {
                        setRefreshFlag(false);
                        clearInterval(window.ExecuteTimer);
                      }}
                      tooltipText="Stop Auto-Refresh"
                      ariaLabel="Stop Auto-Refresh"
                    >
                      <SyncDisabledIcon />
                    </IconContainer>
                  ) : (
                    <IconContainer
                      handleClick={() => {
                        setRefreshFlag(true);
                      }}
                      tooltipText="Enable Auto-Refresh"
                      ariaLabel="Enable Auto-Refresh"
                    >
                      <RefreshIcon />
                    </IconContainer>
                  )}
                </Box>

                <TSMenu
                  isReadOnly={effectiveReadOnly}
                  handleExecuteTest={handleExecuteTest}
                  anchorEl={anchorEl}
                  handleClose={handleMenuClose}
                  setTestCaseSearchKeyword={setTestCaseSearchKeyword}
                  handleDeleteTestSteps={handleDeleteTestSteps}
                  handleInlineTestSteps={handleInlineTestSteps}
                  handleCreateTestCase={handleCreateTestCase}
                  handleChangeToAssert={handleChangeToAssert}
                  handleSetFlow={handleSetFlow}
                  handleSetWaitTime={handleSetWaitTime}
                  handleBulkEditKeywordAction={handleBulkEditKeywordAction}
                  handleSetCondLogic={handleSetCondLogic}
                  handleRemoveConditionalLogic={handleRemoveConditionalLogic}
                  handleFindSimilarSteps={handleFindSimilarSteps}
                  handleSwapSteps={handleSwapSteps}
                  handleViewHiddenDeletedSteps={handleViewHiddenDeletedSteps}
                  selectedStepsCount={checkedItems?.length || 0}
                  setshowDependent={setshowDependent}
                  setshowDependency={setshowDependency}
                />
                <Box onClick={handleMenuClick}>
                  <IconContainer tooltipText="More Options" ariaLabel="More Options Menu">
                    <ContextIcon />
                  </IconContainer>
                </Box>
                <Tooltip title="Test Case Run History">
                  <span>
                    <IconButton
                      disabled={!selectedTestCase}
                      onClick={() => {
                        navigate("/new/reports", {
                          state: {
                            fromTestStudio: true,
                            idproducts: selectedProduct,
                            idmodules: selectedModule,
                            idtest_case: selectedTestCase,
                            Test_Case: testCaseResults.testcase.Test_Case,
                          },
                        });
                      }}
                      aria-label="View Test Case Run History"
                      sx={{
                        backgroundColor: "#2550d1",
                        color: "#fff",
                        boxShadow: "0px 16px 30.6px 0px rgba(37, 80, 209, 0.27)",
                        padding: "10px",
                        "&:hover": {
                          backgroundColor: "#1e40a8",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#ccc",
                          color: "#666",
                        },
                      }}
                    >
                      <HistoryIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                {/* Review Flow - Commented out
                <Tooltip title="Review Test Flow">
                  <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={() => setShowReviewFlowModal(true)}
                    endIcon={<TimelineIcon />}
                    sx={{
                      backgroundColor: "#2550d1",
                      boxShadow: "0px 16px 30.6px 0px rgba(37, 80, 209, 0.27)",
                      padding: "10px 20px",
                    }}
                  >
                    Review Flow
                  </StyledButton>
                </Tooltip>
                */}
                <Tooltip title="Execute in Debug Mode">
                  <IconButton
                    onClick={() => setShowDebugModal(true)}
                    aria-label="Execute Test Case in Debug Mode"
                    sx={{
                      backgroundColor: isDebugMode ? "#2550d1" : "#ff5336",
                      color: "#fff",
                      boxShadow: "0px 16px 30.6px 0px rgba(255, 83, 54, 0.27)",
                      padding: "10px",
                      "&:hover": {
                        backgroundColor: isDebugMode ? "#1e40a8" : "#e04a2f",
                      },
                    }}
                  >
                    <BugReport />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Execute Test Case">
                  <IconButton
                    onClick={() => handleExecuteTest(false)}
                    aria-label="Execute Test Case"
                    sx={{
                      backgroundColor: "#ff5336",
                      color: "#fff",
                      boxShadow: "0px 16px 30.6px 0px rgba(255, 83, 54, 0.27)",
                      padding: "10px",
                      "&:hover": {
                        backgroundColor: "#e04a2f",
                      },
                    }}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box display="flex" justifyContent={"space-between"} alignItems="center">
              <Box display="flex" columnGap={3} alignItems="center">
                <StudioSelection
                  products={products}
                  modules={modules}
                  testCases={testCases}
                  isLoadingTestcase={isLoadingTestcase}
                  setTestCaseSearchKeyword={setTestCaseSearchKeyword}
                  testCaseSearchKeyword={testCaseSearchKeyword}
                  setRefreshFlag={setRefreshFlag}
                  setHasUserClearedFilters={setHasUserClearedFilters}
                />
              </Box>
            </Box>
          </Box>
          {isLoading ? (
            <CircularProgress />
          ) : selectedTestCase && testCaseResults ? (
            <>
              {viewMode === "grid" ? (
                <Box
                  ref={testStepsContainerRef}
                  display="flex"
                  columnGap={4}
                  flexWrap="wrap"
                  rowGap={4}
                  mt={3}
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    minHeight: 0,
                    paddingTop: "130px",
                    alignItems: "flex-start",
                    alignContent: "flex-start",
                  }}
                >
                  {currentItems.map((result, index) => (
                    <TestItemCard
                      item={result}
                      index={indexOfFirstItem + index}
                      key={result.idtest_step}
                      handleEdit={handleEdit}
                      width={itemWidth}
                      isCheckbox={canBulkEdit}
                      isReadOnly={effectiveReadOnly}
                      handleCheckboxChange={(e, item) => handleCheckboxChange(e, item)}
                      checkedItems={checkedItems}
                    />
                  ))}
                </Box>
              ) : (
                <Box
                  ref={testStepsContainerRef}
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    minHeight: 0,
                    paddingTop: "130px",
                  }}
                >
                  <TestTableView
                    testCaseResults={{
                      ...testCaseResults,
                      data: currentItems,
                    }}
                    handleEdit={handleEdit}
                    tsrData={tsrData}
                    isCheckbox={canBulkEdit}
                    isReadOnly={effectiveReadOnly}
                    handleCheckboxChange={handleCheckboxChange}
                    checkedItems={checkedItems}
                    onInlineTestCaseView={handleInlineTestCaseView}
                  />
                </Box>
              )}
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="300px"
              width="100%"
              paddingTop="130px"
            >
              No data available
            </Box>
          )}
        </>
      )}
      {currentEditItemIndex !== null && (
        <>
          <EditHeader
            editItem={editData}
            handleAddStep={addStepQuery}
            handleSaveStep={saveStepQuery}
            handleMerge={handleMerge}
            isReadOnly={effectiveReadOnly}
          />
          <EditTabs editItem={editData} isReadOnly={effectiveReadOnly} />
        </>
      )}
    </Box>
  );
};

export default TestStudio;
